import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getAllTasks, TASK_OFFSETS } from "../../lib/phases";
import { AlertTriangle, Clock, CheckCircle2 } from "lucide-react";

const DAYS_BEFORE = 14;   // include overdue up to 2 weeks back
const DAYS_AFTER = 60;    // future window

function parseISODateLocal(iso) {
  if (!iso) return null;
  // Interpret YYYY-MM-DD as local-midnight to avoid TZ jitter
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function startOfDay(d) {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
}

function daysBetween(a, b) {
  return Math.round((startOfDay(b) - startOfDay(a)) / 86400000);
}

function fmtShort(d) {
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function statusForDeadline(dueDate, today) {
  const diff = daysBetween(today, dueDate);
  if (diff < 0) return "overdue";
  if (diff <= 7) return "due-soon";
  return "future";
}

const DOT_COLORS = {
  overdue: { fill: "#dc2626", ring: "#fee2e2", label: "Overdue" },
  "due-soon": { fill: "#f59e0b", ring: "#fef3c7", label: "Due within 7 days" },
  future: { fill: "#010082", ring: "#e0e7ff", label: "Upcoming" },
};

export default function DashboardTimeline({ listings }) {
  const [hover, setHover] = useState(null);
  const today = useMemo(() => startOfDay(new Date()), []);
  const windowStart = useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() - DAYS_BEFORE);
    return d;
  }, [today]);
  const windowEnd = useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() + DAYS_AFTER);
    return d;
  }, [today]);
  const totalDays = DAYS_BEFORE + DAYS_AFTER;

  // Compute per-listing deadline rows
  const rows = useMemo(() => {
    const allTasks = getAllTasks();
    return listings
      .map((l) => {
        const items = [];
        for (const meta of allTasks) {
          const t = l.tasks?.[meta.id];
          if (!t?.date || t.done) continue;
          const due = parseISODateLocal(t.date);
          if (!due) continue;
          if (due < windowStart || due > windowEnd) continue;
          const dayIndex = daysBetween(windowStart, due);
          const pct = (dayIndex / totalDays) * 100;
          items.push({
            id: `${l.id}::${meta.id}`,
            listingId: l.id,
            taskLabel: meta.label,
            phaseTitle: meta.phaseTitle,
            due,
            pct,
            status: statusForDeadline(due, today),
          });
        }
        items.sort((a, b) => a.due - b.due);
        // Compute closing date position (if within window)
        let closing = null;
        if (l.closing_date) {
          const close = parseISODateLocal(l.closing_date);
          if (close && close >= windowStart && close <= windowEnd) {
            const days = daysBetween(today, close);
            closing = {
              date: close,
              pct: (daysBetween(windowStart, close) / totalDays) * 100,
              status: days < 0 ? "past" : days <= 7 ? "soon" : "future",
              days,
            };
          }
        }
        return {
          listing: l,
          items,
          closing,
          minPct: items.length ? Math.min(...items.map((x) => x.pct)) : null,
          maxPct: items.length ? Math.max(...items.map((x) => x.pct)) : null,
          overdueCount: items.filter((x) => x.status === "overdue").length,
        };
      })
      // Sort listings: those with soonest overdue/due-soon first, then by earliest deadline
      .sort((a, b) => {
        if (b.overdueCount !== a.overdueCount) return b.overdueCount - a.overdueCount;
        const am = a.items[0]?.due?.getTime() ?? Number.POSITIVE_INFINITY;
        const bm = b.items[0]?.due?.getTime() ?? Number.POSITIVE_INFINITY;
        return am - bm;
      });
  }, [listings, windowStart, windowEnd, totalDays, today]);

  // Week tick marks (start of every Monday within the window)
  const ticks = useMemo(() => {
    const out = [];
    const d = new Date(windowStart);
    // Advance to first Monday >= windowStart
    while (d.getDay() !== 1) d.setDate(d.getDate() + 1);
    while (d <= windowEnd) {
      const idx = daysBetween(windowStart, d);
      out.push({ date: new Date(d), pct: (idx / totalDays) * 100 });
      d.setDate(d.getDate() + 7);
    }
    return out;
  }, [windowStart, windowEnd, totalDays]);

  const todayPct = (DAYS_BEFORE / totalDays) * 100;

  const rowsWithItems = rows.filter((r) => r.items.length > 0 || r.closing);
  const emptyRows = rows.filter((r) => r.items.length === 0 && !r.closing);

  return (
    <div data-testid="dashboard-timeline" className="rounded-md border border-stone-200 bg-white p-6">
      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 mb-6 text-xs">
        <span className="overline text-stone-500 mr-2">Legend</span>
        {Object.entries(DOT_COLORS).map(([k, v]) => (
          <span key={k} className="inline-flex items-center gap-1.5">
            <span
              className="inline-block h-2.5 w-2.5 rounded-full ring-4"
              style={{ backgroundColor: v.fill, boxShadow: `0 0 0 3px ${v.ring}` }}
            />
            <span className="text-stone-600">{v.label}</span>
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5 ml-auto text-stone-500">
          <span
            className="inline-block w-4 h-0.5"
            style={{ backgroundColor: "#010082" }}
          />
          Today
        </span>
      </div>

      {/* Date scale */}
      <div className="relative h-10 mb-2 pl-56">
        <div className="absolute inset-x-0 top-0 h-10" style={{ marginLeft: "14rem" }}>
          {ticks.map((t, i) => (
            <div
              key={i}
              className="absolute top-0 flex flex-col items-start"
              style={{ left: `${t.pct}%` }}
            >
              <div className="h-2 w-px bg-stone-300" />
              <span className="mt-1 text-[10px] uppercase tracking-widest text-stone-400 whitespace-nowrap -translate-x-1/2 pl-1">
                {fmtShort(t.date)}
              </span>
            </div>
          ))}
          {/* Today line label */}
          <div
            className="absolute -top-1 -translate-x-1/2 flex flex-col items-center"
            style={{ left: `${todayPct}%` }}
          >
            <span
              className="text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded"
              style={{ backgroundColor: "#010082", color: "#fff" }}
            >
              Today
            </span>
          </div>
        </div>
      </div>

      {/* Rows */}
      <div className="relative">
        {/* Today vertical line spanning all rows */}
        <div
          className="absolute top-0 bottom-0 w-px"
          style={{
            left: `calc(14rem + ${todayPct}% - ${todayPct}%)`,
          }}
        />

        {rowsWithItems.length === 0 && emptyRows.length === 0 && (
          <div className="py-8 text-sm text-stone-500 italic text-center">
            No listings yet — add a home to see the timeline populate.
          </div>
        )}

        {rowsWithItems.map(({ listing, items, overdueCount, closing }) => (
          <div
            key={listing.id}
            data-testid={`timeline-row-${listing.id}`}
            className="flex items-center gap-4 py-3 border-t border-stone-100 first:border-t-0"
          >
            {/* Property label */}
            <Link
              to={`/listings/${listing.id}`}
              className="w-56 shrink-0 group"
              data-testid={`timeline-listing-link-${listing.id}`}
            >
              <div className="flex items-center gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-stone-900 truncate group-hover:text-brand transition-colors">
                    {listing.property_address || "Untitled home"}
                  </div>
                  <div className="text-[11px] uppercase tracking-widest text-stone-400 truncate">
                    {(listing.seller_names || "").split(",")[0] || "—"}
                  </div>
                </div>
                {overdueCount > 0 && (
                  <span
                    data-testid={`timeline-overdue-badge-${listing.id}`}
                    className="inline-flex items-center gap-0.5 rounded-full bg-rose-100 text-rose-700 text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5"
                  >
                    <AlertTriangle className="h-2.5 w-2.5" />
                    {overdueCount}
                  </span>
                )}
              </div>
            </Link>

            {/* Track */}
            <div className="relative flex-1 h-8">
              {/* Baseline */}
              <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-px bg-stone-200" />
              {/* Today line */}
              <div
                className="absolute top-0 bottom-0 w-px"
                style={{ left: `${todayPct}%`, backgroundColor: "#010082", opacity: 0.4 }}
              />
              {/* Closing line */}
              {closing && (
                <div
                  data-testid={`timeline-closing-line-${listing.id}`}
                  className="absolute -top-1 -bottom-1 flex flex-col items-center pointer-events-none"
                  style={{ left: `${closing.pct}%`, transform: "translateX(-50%)" }}
                >
                  <div
                    className="w-px flex-1"
                    style={{
                      backgroundColor:
                        closing.status === "soon"
                          ? "#f59e0b"
                          : closing.status === "past"
                          ? "#78716c"
                          : "#059669",
                    }}
                  />
                  <div
                    className="absolute -top-2 whitespace-nowrap rounded-full text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5"
                    style={{
                      backgroundColor:
                        closing.status === "soon"
                          ? "#f59e0b"
                          : closing.status === "past"
                          ? "#78716c"
                          : "#059669",
                      color: "#fff",
                    }}
                    title={`Target close: ${closing.date.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}`}
                  >
                    Close
                  </div>
                </div>
              )}
              {/* Dots */}
              {items.map((it) => {
                const c = DOT_COLORS[it.status];
                return (
                  <Link
                    key={it.id}
                    to={`/listings/${it.listingId}`}
                    data-testid={`timeline-dot-${it.id}`}
                    onMouseEnter={() => setHover(it)}
                    onMouseLeave={() => setHover((h) => (h && h.id === it.id ? null : h))}
                    className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-3 w-3 rounded-full transition-transform hover:scale-125 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand"
                    style={{
                      left: `${it.pct}%`,
                      backgroundColor: c.fill,
                      boxShadow: `0 0 0 3px ${c.ring}`,
                    }}
                    aria-label={`${it.taskLabel} — due ${fmtShort(it.due)}`}
                  />
                );
              })}
            </div>
          </div>
        ))}

        {emptyRows.length > 0 && (
          <div className="mt-6 pt-4 border-t border-dashed border-stone-200">
            <p className="text-[11px] uppercase tracking-widest text-stone-400 mb-2">
              No deadlines in the next {DAYS_AFTER} days
            </p>
            <ul className="flex flex-wrap gap-x-4 gap-y-1">
              {emptyRows.map(({ listing }) => (
                <li key={listing.id}>
                  <Link
                    to={`/listings/${listing.id}`}
                    className="text-sm text-stone-500 hover:text-brand"
                  >
                    {listing.property_address || "Untitled home"}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Hover tooltip (fixed bottom-right so it never gets clipped) */}
      {hover && (
        <div
          data-testid="timeline-hover-card"
          className="mt-6 rounded-md border border-stone-200 bg-stone-50 p-4"
        >
          <div className="flex items-center gap-2 mb-1">
            {hover.status === "overdue" ? (
              <AlertTriangle className="h-4 w-4 text-rose-600" />
            ) : hover.status === "due-soon" ? (
              <Clock className="h-4 w-4 text-amber-600" />
            ) : (
              <CheckCircle2 className="h-4 w-4 text-brand" />
            )}
            <span className="text-[11px] uppercase tracking-widest text-stone-500">
              {DOT_COLORS[hover.status].label} · {fmtShort(hover.due)}
            </span>
          </div>
          <div className="text-sm font-semibold text-stone-900">{hover.taskLabel}</div>
          <div className="text-xs text-stone-500 mt-0.5">{hover.phaseTitle}</div>
        </div>
      )}
    </div>
  );
}
